import { Button, Input, RefreshIcon } from "@jutro/ui";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ConfirmDialog } from "components/new/ConfirmDialog";
import {
  EdmGetAllStaffWithGenreAndWorkingHoursDocument,
  EdmGetStaffVisitDurationDocument,
  EdmGetStaffVisitDurationQuery,
  EdmMedicalPerson,
  EdmVisitTimeDuration,
  useEdmGetDefaultVisitDurationQuery,
  useEdmGetStaffVisitDurationQuery,
  useEdmUpdateStaffVisitDurationMutation,
} from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";
import {
  defaultVisitsTimeDuration,
  genreToFieldsMap,
  transformedUserGenre,
} from "views/StaffSchedule/StaffSettings/tools";
import { staffIdsWithNurseVisitTypes } from "views/StaffSchedule/tools";

type Props = {
  selectedStaff: EdmMedicalPerson;
};

export const VisitsDurationForm = ({ selectedStaff }: Props) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const {
    data: staffVisitDurationData,
    loading: getStaffVisitDurationLoading,
  } = useEdmGetStaffVisitDurationQuery({
    variables: { staffId: selectedStaff.id },
  });

  const { data: defaultVisitDurationData } = useEdmGetDefaultVisitDurationQuery(
    {
      fetchPolicy: "cache-and-network",
    },
  );

  const placeholderData =
    defaultVisitDurationData?.edmGetDefaultVisitDuration ?? {};

  const [
    updateStaffVisitDuration,
    { loading: updateStaffVisitDurationLoading },
  ] = useEdmUpdateStaffVisitDurationMutation({
    refetchQueries: [
      EdmGetStaffVisitDurationDocument,
      EdmGetAllStaffWithGenreAndWorkingHoursDocument,
    ],
  });

  const {
    control,
    formState: { isDirty },
    reset,
    handleSubmit,
  } = useForm<EdmGetStaffVisitDurationQuery["edmGetStaffVisitDuration"]>({
    defaultValues: {},
  });

  useEffect(() => {
    if (staffVisitDurationData && !getStaffVisitDurationLoading) {
      reset(staffVisitDurationData.edmGetStaffVisitDuration);
    }
  }, [staffVisitDurationData, getStaffVisitDurationLoading, reset]);

  const fields = useMemo(() => {
    const baseFields = [
      ...genreToFieldsMap[transformedUserGenre(selectedStaff)],
    ];

    if (staffIdsWithNurseVisitTypes.includes(selectedStaff.id)) {
      const additionalNurseFields = genreToFieldsMap["NURSE"];

      return [...baseFields, ...additionalNurseFields];
    }

    return baseFields;
  }, [selectedStaff.id]);

  const onSubmit = async (
    input: EdmVisitTimeDuration,
    mode: "UPDATE" | "RESET",
  ) => {
    const staffId = selectedStaff.id;

    if (mode === "RESET") {
      await toaster.promise(
        updateStaffVisitDuration({
          variables: { staffId, input: defaultVisitsTimeDuration },
        }),
        {
          error: "Nie udało się zresetować czasów trwania wizyt",
          loading: "Resetuję czasy trwania wizyt",
          success: "Zresetowano czasy trwania wizyt",
        },
      );

      return;
    }

    if (!isDirty) {
      return;
    }

    await toaster.promise(
      updateStaffVisitDuration({
        variables: { staffId, input },
      }),
      {
        error: "Nie udało się zaktualizować czasów trwania wizyt",
        loading: "Aktualizuję czasy trwania wizyt",
        success: "Zaktualizowano czasy trwania wizyt",
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-8 p-4">
        <div className="font-paragraph-1 flex flex-col">
          <p>
            Długość trwania wizyty danego pracownika będzie taka sama we
            wszystkich placówkach.
          </p>
          <p>
            Zmiana długości trwania wizyty nie wpłynie na zmiany, które zostały
            już utworzone.
          </p>
        </div>

        <form className="flex w-full flex-wrap gap-4">
          {fields.map((field) => (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              render={({ field: { value, onChange } }) => (
                <div>
                  <Input
                    label={field.label}
                    type="number"
                    max={59}
                    maxLength={2}
                    sideElement="min"
                    sideElementPosition="end"
                    placeholder={
                      field.name in placeholderData &&
                      placeholderData[field.name]
                        ? String(placeholderData[field.name])
                        : ""
                    }
                    value={value ?? ""}
                    onChange={(e) => {
                      if (Number(e.target.value) > 59) {
                        return;
                      }

                      if (Number(e.target.value) === 0) {
                        return onChange(null);
                      }
                      onChange(Number(e.target.value));
                    }}
                  />
                </div>
              )}
            />
          ))}
        </form>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            text="Przywróć domyślne"
            full={false}
            icon={<RefreshIcon />}
            loading={updateStaffVisitDurationLoading}
            onClick={() => setIsConfirmationModalOpen(true)}
          />

          <Button
            type="button"
            variant="secondary"
            text="Anuluj"
            full={false}
            disabled={updateStaffVisitDurationLoading}
            onClick={() =>
              reset(staffVisitDurationData?.edmGetStaffVisitDuration)
            }
          />

          <Button
            type="button"
            variant="primary"
            text="Zapisz"
            full={false}
            loading={updateStaffVisitDurationLoading}
            onClick={handleSubmit((data) => onSubmit(data, "UPDATE"))}
          />
        </div>
      </div>

      <ConfirmDialog
        open={isConfirmationModalOpen}
        title="Przywróć domyślne czasy wizyt"
        text="Czy na pewno chcesz przywrócić domyślne czasy wizyty?"
        confirmText="Przywróć domyślne"
        loading={updateStaffVisitDurationLoading}
        setOpen={setIsConfirmationModalOpen}
        onConfirm={handleSubmit((data) => onSubmit(data, "RESET"))}
      />
    </>
  );
};
